import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchBySlug } from '../lib/cms';
import { Phone, Mail, ChevronRight, ArrowRight, ShieldCheck, Star, Sparkles } from 'lucide-react';
import GalleryView from '../components/GalleryView';

const ALL_SERVICES = [
  { name: 'Tømrer', slug: 'toemrer' },
  { name: 'Tag', slug: 'tag' },
  { name: 'Døre & Vinduer', slug: 'doere-vinduer' },
  { name: 'Tilbygning', slug: 'tilbygning' },
  { name: 'Gulve', slug: 'gulve' },
  { name: 'Montage', slug: 'montage' },
  { name: 'Hovedentrepriser', slug: 'hovedentrepriser' },
];

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadServiceData() {
      setLoading(true);
      // Hent siden baseret på slug
      const page = await fetchBySlug(slug);
      
      if (!page || page.collectionName !== 'pages') {
        // Hvis siden ikke findes, omdiriger til 404
        navigate('/404', { replace: true });
        return;
      }

      setPageData(page);

      // Hent det tilknyttede galleri, hvis der er angivet en gallery_slug
      if (page.gallery_slug) {
        const gallery = await fetchBySlug(page.gallery_slug);
        if (gallery && gallery.collectionName === 'galleries') {
          setGalleryData(gallery);
        } else {
          setGalleryData(null);
        }
      } else {
        setGalleryData(null);
      }
      
      setLoading(false);
    }

    loadServiceData();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
      </div>
    );
  }

  if (!pageData) return null;

  const title = pageData.title?.da || '';
  const metaTitle = pageData.meta_title?.da || `${title} - KBS Tømrer – Montage ApS`;
  const metaDesc = pageData.meta_description?.da || '';
  const heroImage = pageData.hero_image?.[0] || 'https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/baggrund-22.jpg';

  // Optimer hero-billede via Cloudflare resizing (~1600px bredde)
  const optimizedHero = heroImage.startsWith('https://assets.auxodev.dk')
    ? heroImage.replace('https://assets.auxodev.dk/assets/', 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/')
    : heroImage;

  const relatedServices = ALL_SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <html lang="da" />
      </Helmet>

      {/* Hero Header */}
      <section className="relative bg-slate-950 text-white py-24 lg:py-32">
        <div className="absolute inset-0 z-0">
          <img
            src={optimizedHero}
            alt={title}
            className="h-full w-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950 to-slate-950" />
        </div>

        <div className="relative z-10 mx-auto max-w-5xl px-4 text-center">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold text-slate-400 mb-4 uppercase tracking-wider">
            <Link to="/" className="hover:text-white transition-colors">Forside</Link>
            <ChevronRight className="h-4 w-4 text-slate-600" />
            <span className="text-brand-primary-light">Ydelser</span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            {title}
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Professionel løsning udført med stolthed og mere end 30 års erfaring.
          </p>
        </div>
      </section>

      {/* Main Content & Sidebar */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Column: Rich Text Content */}
            <div className="lg:col-span-8">
              <div className="prose prose-slate max-w-none prose-headings:font-display prose-headings:font-bold prose-headings:text-brand-charcoal prose-p:leading-relaxed prose-p:text-slate-600 prose-strong:text-brand-charcoal prose-ul:list-disc prose-ul:pl-6 prose-li:text-slate-600">
                {pageData.content?.da ? (
                  <div dangerouslySetInnerHTML={{ __html: pageData.content.da }} />
                ) : (
                  <p>Indholdet kunne ikke indlæses.</p>
                )}
              </div>

              {/* Dynamic Gallery Section */}
              {galleryData && galleryData.images && galleryData.images.length > 0 && (
                <div className="mt-16 border-t border-slate-100 pt-12">
                  <h3 className="font-display text-2xl font-bold text-brand-charcoal mb-2 flex items-center gap-2">
                    <Sparkles className="h-6 w-6 text-brand-primary" />
                    Billeder fra vores arbejde med {title.toLowerCase()}
                  </h3>
                  <p className="text-sm text-slate-500 mb-8">
                    Her kan du se eksempler på projekter, vi har udført for vores tilfredse kunder. Klik på et billede for stor visning.
                  </p>
                  <GalleryView images={galleryData.images} title={title} />
                </div>
              )}
            </div>

            {/* Right Column: Sidebar Contact & Related */}
            <div className="lg:col-span-4">
              <div className="sticky top-28 space-y-8">
                {/* Contact Box */}
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8 shadow-md">
                  <h3 className="font-display text-lg font-bold text-brand-charcoal mb-4 pb-3 border-b border-slate-200">
                    Få et uforpligtende tilbud
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-6">
                    Har du spørgsmål til {title.toLowerCase()}, eller ønsker du et uforpligtende tilbud på dit projekt? Ring eller skriv til os i dag.
                  </p>

                  <div className="space-y-4">
                    <a
                      href="tel:51219030"
                      className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 hover:border-brand-primary/30 hover:shadow-md transition-all group"
                    >
                      <div className="rounded-lg bg-brand-primary/10 p-2.5 text-brand-primary group-hover:bg-brand-primary group-hover:text-white transition-colors">
                        <Phone className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase">Ring direkte</p>
                        <p className="text-base font-bold text-brand-charcoal group-hover:text-brand-primary transition-colors">
                          51 21 90 30
                        </p>
                      </div>
                    </a>

                    <a
                      href="mailto:kbsoerensen@outlook.dk"
                      className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 hover:border-brand-primary/30 hover:shadow-md transition-all group"
                    >
                      <div className="rounded-lg bg-brand-primary/10 p-2.5 text-brand-primary group-hover:bg-brand-primary group-hover:text-white transition-colors">
                        <Mail className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-400 uppercase">Send en mail</p>
                        <p className="text-sm font-bold text-brand-charcoal group-hover:text-brand-primary transition-colors truncate">
                          kbsoerensen@outlook.dk
                        </p>
                      </div>
                    </a>
                  </div>

                  <Link
                    to="/kontakt"
                    className="mt-6 w-full flex items-center justify-center gap-2 rounded-xl bg-brand-primary hover:bg-brand-primary-dark px-4 py-3 text-sm font-bold text-white shadow transition-colors cursor-pointer"
                  >
                    Gå til kontaktformular <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>

                {/* Related Services */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-md">
                  <h3 className="font-display text-base font-bold text-brand-charcoal mb-4 pb-3 border-b border-slate-200">
                    Andre ydelser
                  </h3>
                  <ul className="space-y-3">
                    {relatedServices.map((service) => (
                      <li key={service.slug}>
                        <Link
                          to={`/ydelser/${service.slug}`}
                          className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 text-sm font-semibold text-slate-700 hover:text-brand-primary transition-colors group"
                        >
                          <span>{service.name}</span>
                          <ChevronRight className="h-4 w-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
